/**
 * Mobile emulation for the preview iframe.
 *
 * The phone frame in the editor is just a visual wrapper — the browser
 * running inside the iframe still sees a desktop mouse. Themes that
 * ship carousels, image galleries, drawer-swipes or any interaction
 * gated on `touchstart/touchmove/touchend` (or on
 * `navigator.maxTouchPoints > 0`) never fire their touch handlers
 * when the user drags with a trackpad/mouse, so the experience is
 * broken for anyone testing on a laptop.
 *
 * This module installs a lightweight touch emulator that:
 *
 *   1. Flags the iframe window as touch-capable — `maxTouchPoints`,
 *      `ontouchstart`, `PointerEvent` compatibility — so libraries
 *      that branch on capability detection pick the touch path.
 *
 *   2. Swaps the user-agent string to a modern mobile UA. Themes
 *      that do runtime user-agent sniffing (rare but real) behave
 *      accordingly; CSS media queries keep working because the
 *      iframe is already sized to a phone viewport.
 *
 *   3. Synthesises TouchEvents from primary-button mouse drags.
 *      `mousedown` → `touchstart`, `mousemove` (while held) →
 *      `touchmove`, `mouseup` → `touchend`. Single finger, one
 *      identifier per gesture. Real mouse events still fire so
 *      click-based navigation and form interactions remain
 *      unaffected — most carousel libraries listen to both and
 *      pick the first one that arrives.
 *
 * Uninstall reverses every override it can (event listeners,
 * maxTouchPoints, UA). Some overrides (window.ontouchstart) are
 * write-once on many browsers and stick until the iframe reloads;
 * that's acceptable because switching back to desktop already
 * implies a UX where touch capability doesn't hurt.
 */

import type { IframeHandle } from './iframe-bridge';

const MOBILE_UA =
  'Mozilla/5.0 (Linux; Android 14; Pixel 8) AppleWebKit/537.36 ' +
  '(KHTML, like Gecko) Chrome/147.0.0.0 Mobile Safari/537.36';

export interface MobileEmulatorHandle {
  /** Tears down listeners and reverses overrides where possible. */
  uninstall(): void;
}

export function installMobileEmulator(
  handle: IframeHandle
): MobileEmulatorHandle {
  const { doc, win } = handle;

  // ----- 1. Touch capability flags --------------------------------------
  const restoreNav = overrideNavigatorProps(win);

  // ----- 2. User-agent swap ---------------------------------------------
  //   Some themes branch on UA in JS. CSS media queries already work
  //   because the iframe viewport is sized to a phone, so we don't need
  //   to touch those.
  const restoreUA = overrideUserAgent(win);

  // ----- 3. Mouse → Touch synthesis -------------------------------------
  let dragging = false;
  let currentTarget: EventTarget | null = null;

  function dispatchTouch(
    type: 'touchstart' | 'touchmove' | 'touchend' | 'touchcancel',
    source: MouseEvent
  ): void {
    const target = currentTarget || (source.target as EventTarget | null);
    if (!target) return;

    // Build a Touch object. Some browsers throw on `new Touch()` — fall
    // back to a plain touch-like object which most libraries accept.
    let touch: Touch;
    try {
      touch = new (win as any).Touch({
        identifier: 1,
        target,
        clientX: source.clientX,
        clientY: source.clientY,
        pageX: source.pageX,
        pageY: source.pageY,
        screenX: source.screenX,
        screenY: source.screenY,
        radiusX: 2,
        radiusY: 2,
        rotationAngle: 0,
        force: 1,
      });
    } catch {
      touch = {
        identifier: 1,
        target,
        clientX: source.clientX,
        clientY: source.clientY,
        pageX: source.pageX,
        pageY: source.pageY,
        screenX: source.screenX,
        screenY: source.screenY,
        radiusX: 2,
        radiusY: 2,
        rotationAngle: 0,
        force: 1,
      } as Touch;
    }

    const active = type === 'touchend' || type === 'touchcancel' ? [] : [touch];

    let ev: TouchEvent;
    try {
      ev = new (win as any).TouchEvent(type, {
        bubbles: true,
        cancelable: true,
        composed: true,
        touches: active,
        targetTouches: active,
        changedTouches: [touch],
        view: win as any,
      });
    } catch {
      // Fallback for browsers without a usable TouchEvent constructor:
      // fire a CustomEvent carrying the same payload. Most libraries
      // rely on the object shape, not the constructor type.
      ev = new (win as any).CustomEvent(type, {
        bubbles: true,
        cancelable: true,
        detail: { touches: active, targetTouches: active, changedTouches: [touch] },
      });
      (ev as any).touches = active;
      (ev as any).targetTouches = active;
      (ev as any).changedTouches = [touch];
    }

    target.dispatchEvent(ev);
  }

  function onMouseDown(e: MouseEvent) {
    if (e.button !== 0) return; // primary button only
    dragging = true;
    currentTarget = e.target as EventTarget | null;
    dispatchTouch('touchstart', e);
  }

  function onMouseMove(e: MouseEvent) {
    if (!dragging) return;
    dispatchTouch('touchmove', e);
  }

  function onMouseUp(e: MouseEvent) {
    if (!dragging) return;
    dragging = false;
    dispatchTouch('touchend', e);
    currentTarget = null;
  }

  function onMouseLeave(e: MouseEvent) {
    // Mouse left the iframe while dragging — treat as touchcancel so
    // carousels return to a stable state instead of staying "stuck".
    if (!dragging) return;
    dragging = false;
    dispatchTouch('touchcancel', e);
    currentTarget = null;
  }

  // Capture-phase so we beat app-level handlers and don't depend on
  // bubbling order.
  doc.addEventListener('mousedown', onMouseDown, true);
  doc.addEventListener('mousemove', onMouseMove, true);
  doc.addEventListener('mouseup', onMouseUp, true);
  doc.addEventListener('mouseleave', onMouseLeave, true);

  return {
    uninstall() {
      doc.removeEventListener('mousedown', onMouseDown, true);
      doc.removeEventListener('mousemove', onMouseMove, true);
      doc.removeEventListener('mouseup', onMouseUp, true);
      doc.removeEventListener('mouseleave', onMouseLeave, true);
      restoreNav();
      restoreUA();
    },
  };
}

// ---------------------------------------------------------------------------
// Overrides
// ---------------------------------------------------------------------------

function overrideNavigatorProps(win: Window): () => void {
  const prevMaxTouch = Object.getOwnPropertyDescriptor(
    Object.getPrototypeOf(win.navigator),
    'maxTouchPoints'
  );
  try {
    Object.defineProperty(win.navigator, 'maxTouchPoints', {
      configurable: true,
      get: () => 5,
    });
  } catch {}
  // Set a truthy `ontouchstart` on the window so libraries that use
  //   `'ontouchstart' in window`
  // for capability detection take the touch branch. Writing null is
  // enough — the `in` check only looks for the property's existence.
  let hadOntouchstart = 'ontouchstart' in win;
  if (!hadOntouchstart) {
    try {
      (win as any).ontouchstart = null;
    } catch {}
  }
  return () => {
    if (prevMaxTouch) {
      try {
        Object.defineProperty(
          Object.getPrototypeOf(win.navigator),
          'maxTouchPoints',
          prevMaxTouch
        );
      } catch {}
    }
    if (!hadOntouchstart) {
      try {
        delete (win as any).ontouchstart;
      } catch {}
    }
  };
}

function overrideUserAgent(win: Window): () => void {
  const proto = Object.getPrototypeOf(win.navigator);
  const prev = Object.getOwnPropertyDescriptor(proto, 'userAgent');
  try {
    Object.defineProperty(win.navigator, 'userAgent', {
      configurable: true,
      get: () => MOBILE_UA,
    });
  } catch {}
  return () => {
    if (prev) {
      try {
        Object.defineProperty(win.navigator, 'userAgent', prev);
      } catch {}
    }
  };
}
